import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { ErrorBox, FormattedText, StatusBadge, fmtDate, issueNo, useIssueRowColor } from '../components';
import type { IssueHeader, Version } from '../types';
import { t, tp } from '../i18n';

type Entry = { version: Version & { project_name: string }; issues: IssueHeader[]; resolved?: number; total?: number };

function useScope() {
  const { projectId, project } = useMeta();
  const [params] = useSearchParams();
  const pid = params.has('project') ? Number(params.get('project')) : projectId;
  return { pid, name: pid ? project(pid)?.name : t('All Projects') };
}

export function RoadmapPage() {
  const { pid, name } = useScope();
  const { data, error, isLoading } = useQuery({
    queryKey: ['roadmap', pid],
    queryFn: () => api.get<Entry[]>('/api/roadmap' + qs({ project: pid || undefined })),
  });
  return (
    <div>
      <div className="page-head">
        <h1>
          {t('Roadmap')} <span className="muted">— {name}</span>
        </h1>
      </div>
      <ErrorBox error={error} />
      {isLoading && <div className="muted">{t('Loading…')}</div>}
      {data?.length === 0 && <div className="panel muted">{t('No unreleased versions with planned issues.')}</div>}
      {data?.map((e) => {
        const pct = e.total ? Math.round((100 * (e.resolved ?? 0)) / e.total) : 0;
        return (
          <section key={e.version.id} className="panel">
            <div className="panel-head">
              <h2>
                {e.version.project_name} - {e.version.version}{' '}
                <span className="muted small">{e.version.date_order ? t('(scheduled {date})', { date: fmtDate(e.version.date_order, false) }) : ''}</span>
              </h2>
              <Link className="small" to={'/issues' + qs({ project: e.version.project_id, target_version: e.version.version, hide_status: 0 })}>
                {t('View issues')}
              </Link>
            </div>
            <div className="panel-body">
              {e.version.description && <FormattedText text={e.version.description} className="muted" />}
              <div className="progress" title={t('{resolved} of {total} resolved', { resolved: e.resolved, total: e.total })}>
                <div style={{ width: `${pct}%` }} />
                <span>
                  {pct}% — {tp(e.total ?? 0, '{resolved} of {n} issue resolved', '{resolved} of {n} issues resolved', { resolved: e.resolved ?? 0 })}
                </span>
              </div>
              <IssueLines issues={e.issues} strikeResolved />
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function ChangelogPage() {
  const { pid, name } = useScope();
  const { data, error, isLoading } = useQuery({
    queryKey: ['changelog', pid],
    queryFn: () => api.get<Entry[]>('/api/changelog' + qs({ project: pid || undefined })),
  });
  return (
    <div>
      <div className="page-head">
        <h1>
          {t('Change Log')} <span className="muted">— {name}</span>
        </h1>
      </div>
      <ErrorBox error={error} />
      {isLoading && <div className="muted">{t('Loading…')}</div>}
      {data?.length === 0 && <div className="panel muted">{t('No released versions.')}</div>}
      {data?.map((e) => (
        <section key={e.version.id} className="panel">
          <div className="panel-head">
            <h2>
              {e.version.project_name} - {e.version.version}{' '}
              <span className="muted small">{t('(released {date})', { date: fmtDate(e.version.date_order, false) })}</span>
            </h2>
            <span className="muted small">{tp(e.issues.length, '{n} issue', '{n} issues')}</span>
          </div>
          <div className="panel-body">
            {e.version.description && <FormattedText text={e.version.description} className="muted" />}
            <IssueLines issues={e.issues} />
          </div>
        </section>
      ))}
    </div>
  );
}

function IssueLines({ issues, strikeResolved }: { issues: IssueHeader[]; strikeResolved?: boolean }) {
  const rowColor = useIssueRowColor();
  if (issues.length === 0) return <p className="muted small">{t('No issues.')}</p>;
  return (
    <ul className="issue-lines">
      {issues.map((i) => {
        const row = rowColor(i.status);
        return (
          <li key={i.id} className={`${row.className} ${strikeResolved && i.status >= 80 ? 'done' : ''}`.trim()} style={row.style}>
            <Link to={`/issues/${i.id}`} className="mono">
              {issueNo(i.id)}
            </Link>
            {i.category_name && <span className="muted">[{i.category_name}]</span>} {i.summary}{' '}
            {i.handler_name && <span className="muted">({i.handler_name})</span>} <StatusBadge status={i.status} resolution={i.resolution} />
          </li>
        );
      })}
    </ul>
  );
}
